import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  List,
  ListItemButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Navigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../api/auth/useAuth";
import { useHttp } from "../../api/http/useHttp";
import { IContractResponse, transformContract } from "../../api/hooks/useContracts";
import { useAdminChats } from "./useAdminChats";
import { useChatAccess } from "./useChatAccess";
import { ChatViewer } from "./ChatViewer";

const KIND_LABELS: Record<string, string> = {
  consumer_supplier: "Consumer / Supplier",
  consumer_broker: "Consumer / Broker",
  supplier_broker: "Supplier / Broker",
  group: "All parties",
};

export function ChatsPage() {
  const allowed = useChatAccess();
  return allowed ? <ChatsWorkspace /> : <Navigate to="/" replace />;
}

function ChatsWorkspace() {
  const [params, setParams] = useSearchParams();
  const contract = params.get("contract") || "";
  const conversationId = params.get("conversation") || "";
  const search = params.get("q") || "";
  const unread = params.get("unread") === "true";
  const rawPage = Number(params.get("page") || 0);
  const page = Number.isInteger(rawPage) && rawPage >= 0 && rawPage <= 100_000 ? rawPage : 0;
  const { contracts, conversations } = useAdminChats(
    { page, search, unread_only: unread },
    contract,
  );
  const { client } = useHttp();
  const { self } = useAuth();
  // Optional enrichment is independent of the central chat requests. Node failures cannot hide chats.
  const names = useQuery({
    queryKey: ["admin-chats", self?.email, "names"],
    queryFn: async ({ signal }) => {
      const { data } = await client.post<IContractResponse[]>(
        "/contracts",
        { customers_only: false, show_hidden: true },
        { signal },
      );
      return Object.fromEntries(
        data.map(transformContract).map((c) => [c.shared_uuid, c.contract_display_name]),
      );
    },
    retry: false,
    staleTime: 5 * 60_000,
    gcTime: 0,
    placeholderData: undefined,
  });
  const update = (values: Record<string, string>, replace = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        Object.entries(values).forEach(([key, value]) =>
          value ? next.set(key, value) : next.delete(key),
        );
        return next;
      },
      { replace },
    );
  const conversation = conversations.data?.conversations.find((c) => c.id === conversationId);
  const unavailable =
    [403, 404].includes(conversations.error?.response?.status || 0) ||
    (Boolean(conversationId) && conversations.isSuccess && !conversation);

  return (
    <Stack
      className="sentry-block"
      data-private="true"
      height="100%"
      minHeight={0}
      p={{ xs: 2, md: 3 }}
      gap={2}
    >
      <Box>
        <Typography variant="h5" component="h1">
          Chats
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Active contract conversations · Read-only
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(240px, 1fr) minmax(0, 2fr)" },
          minHeight: 0,
          flex: 1,
          gap: 2,
        }}
      >
        <Stack
          gap={1}
          minHeight={0}
          sx={{ display: { xs: contract ? "none" : "flex", md: "flex" } }}
        >
          <TextField
            size="small"
            label="Search participants or contract UUID"
            value={search}
            inputProps={{ maxLength: 300 }}
            onChange={(event) => update({ q: event.target.value, page: "" }, true)}
          />
          <FormControlLabel
            label="Unread only"
            control={
              <Checkbox
                checked={unread}
                onChange={(_, checked) => update({ unread: checked ? "true" : "", page: "" })}
              />
            }
          />
          {contracts.isError ? (
            <Alert severity="error">
              Unable to load chats.<Button onClick={() => void contracts.refetch()}>Retry</Button>
            </Alert>
          ) : contracts.isPending ? (
            <CircularProgress aria-label="Loading contracts" />
          ) : (
            <List
              aria-label="Contracts with chats"
              sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}
            >
              {!contracts.data?.contracts.length && (
                <Typography color="text.secondary">
                  {unread ? "No unread chats." : "No chats found."}
                </Typography>
              )}
              {contracts.data?.contracts.map((item) => (
                <ListItemButton
                  key={item.contract_uuid}
                  selected={contract === item.contract_uuid}
                  onClick={() => update({ contract: item.contract_uuid, conversation: "" })}
                  sx={{ alignItems: "flex-start", borderRadius: 1, mb: 1 }}
                >
                  <Stack gap={0.5} minWidth={0} width="100%">
                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle2" sx={{ overflowWrap: "anywhere" }}>
                        {names.data?.[item.contract_uuid] ||
                          item.participants.map((p) => p.name).join(" / ") ||
                          item.contract_uuid}
                      </Typography>
                      {item.unread_count > 0 && (
                        <Chip
                          size="small"
                          color="primary"
                          label={item.unread_count}
                          aria-label={`${item.unread_count} unread messages`}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {item.contract_uuid}
                    </Typography>
                    <Typography variant="body2" noWrap>
                      {item.latest_preview}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.latest_at).toLocaleString()}
                    </Typography>
                  </Stack>
                </ListItemButton>
              ))}
            </List>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              disabled={page === 0 || contracts.isFetching}
              onClick={() => update({ page: String(page - 1) })}
            >
              Previous
            </Button>
            <Typography variant="caption">Page {page + 1}</Typography>
            <Button
              disabled={!contracts.data?.has_more || contracts.isFetching}
              onClick={() => update({ page: String(page + 1) })}
            >
              Next
            </Button>
          </Stack>
        </Stack>
        <Stack
          minWidth={0}
          minHeight={0}
          gap={1}
          sx={{ display: { xs: contract ? "flex" : "none", md: "flex" } }}
        >
          {contract ? (
            <>
              <Button
                sx={{ alignSelf: "flex-start", display: { md: "none" } }}
                onClick={() => update({ contract: "", conversation: "" })}
              >
                Back to contracts
              </Button>
              <Typography variant="subtitle1" sx={{ overflowWrap: "anywhere" }}>
                {names.data?.[contract] || contract}
              </Typography>
              {conversations.isPending ? (
                <CircularProgress aria-label="Loading conversations" />
              ) : conversations.isError && !unavailable ? (
                <Alert severity="error">
                  Unable to load conversations. Please retry.
                  <Button onClick={() => void conversations.refetch()}>Retry</Button>
                </Alert>
              ) : unavailable ? (
                <Alert severity="warning">
                  This conversation is no longer available.
                  <Button
                    onClick={() => {
                      update({ conversation: "" });
                      void conversations.refetch();
                    }}
                  >
                    Refresh
                  </Button>
                </Alert>
              ) : (
                <>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    role="group"
                    aria-label="Contract conversations"
                  >
                    {conversations.data?.conversations.map((item) => (
                      <Button
                        key={item.id}
                        variant={item.id === conversationId ? "contained" : "outlined"}
                        aria-pressed={item.id === conversationId}
                        onClick={() => update({ conversation: item.id })}
                      >
                        {KIND_LABELS[item.kind] || item.kind}
                        {item.unread_count > 0 ? ` (${item.unread_count} unread)` : ""}
                      </Button>
                    ))}
                  </Stack>
                  {conversation ? (
                    <ChatViewer
                      key={`${self?.email}:${conversation.id}`}
                      conversation={conversation}
                    />
                  ) : (
                    <Typography color="text.secondary">
                      Select a conversation to read its messages.
                    </Typography>
                  )}
                </>
              )}
            </>
          ) : (
            <Typography color="text.secondary">
              Select a contract to view its conversations.
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}
